import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { GripVertical, Trash2, Settings2, Plus, Eye, Save, ChevronUp, ChevronDown, GitBranch, CheckCircle2 } from "lucide-react";
import { createDefaultField, fetchFormDraft, normalizeFormState, publishForm, saveFormDraft } from "@/lib/form-builder";
import { loadTicketEngine, syncTicketEngine } from "@/lib/ticket-engine";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";

const FIELD_LIBRARY = [
  { type: "short_answer", label: "Short answer" },
  { type: "long_answer", label: "Long answer" },
  { type: "paragraph", label: "Paragraph text" },
  { type: "email", label: "Email" },
  { type: "phone", label: "Phone number" },
  { type: "number", label: "Number" },
  { type: "url", label: "Website URL" },
  { type: "date", label: "Date" },
  { type: "time", label: "Time" },
  { type: "datetime", label: "Date & time" },
  { type: "select", label: "Single select" },
  { type: "dropdown", label: "Dropdown" },
  { type: "multiselect", label: "Multi select" },
  { type: "radio", label: "Multiple choice (single)" },
  { type: "checkbox_group", label: "Multiple choice (multi)" },
  { type: "checkbox", label: "Checkbox" },
  { type: "yes_no", label: "Yes/No" },
  { type: "linear_scale", label: "Linear scale" },
  { type: "rating", label: "Rating" },
  { type: "nps", label: "Net promoter score (0-10)" },
  { type: "address", label: "Address" },
  { type: "country", label: "Country" },
  { type: "consent", label: "Consent / declaration" },
  { type: "section", label: "Section heading" },
  { type: "hidden", label: "Hidden field" },
  { type: "file", label: "File upload" },
];

const DATA_SOURCE_OPTIONS = [
  { key: "manual", label: "Manual input (default)" },
  { key: "attendee.name", label: "Attendee name" },
  { key: "attendee.email", label: "Attendee email" },
  { key: "attendee.company", label: "Attendee company" },
  { key: "attendee.role", label: "Attendee role" },
  { key: "ticket.name", label: "Ticket name" },
  { key: "event.title", label: "Event title" },
  { key: "event.slug", label: "Event slug" },
];

export default function FormBuilder() {
  const { id } = useParams();
  const e = useEventMeta(id);
  const [state, setState] = useState(() => normalizeFormState({ fields: [] }));
  const [statusLabel, setStatusLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [ticketTargets, setTicketTargets] = useState([]);
  const lastSavedHashRef = useRef("");
  const saveTimerRef = useRef(null);
  const statusTimerRef = useRef(null);
  const fields = useMemo(() => state.fields || [], [state.fields]);
  const active = state.active;
  const setFields = (next) =>
    setState((prev) => normalizeFormState({ ...prev, fields: typeof next === "function" ? next(prev.fields || []) : next }));
  const setActive = (next) => setState((prev) => ({ ...prev, active: next }));
  const activeField = fields.find((f) => f.id === active);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const draft = await fetchFormDraft(id);
        if (!cancel) {
          setState(draft);
          lastSavedHashRef.current = JSON.stringify(draft);
        }
      } catch {
        if (!cancel) {
          setState(normalizeFormState({ fields: [] }));
          toast.error("Could not load form draft");
        }
      } finally {
        if (!cancel) setLoading(false);
      }
      try {
        await syncTicketEngine(e.id);
      } catch {
        // Fall back to cached ticket engine.
      }
      const eng = loadTicketEngine(e.id);
      if (!cancel) setTicketTargets((eng.tickets || []).filter((t) => t.type === "TIER").map((t) => ({ id: t.id, name: t.name })));
    })();
    return () => {
      cancel = true;
    };
  }, [id, e.id]);

  useEffect(() => {
    const reload = async () => {
      try {
        const draft = await fetchFormDraft(id);
        setState(draft);
        lastSavedHashRef.current = JSON.stringify(draft);
      } catch {
        // Keep current editor state if refresh fails.
      }
    };
    const onExternal = (ev) => {
      if (ev?.detail?.key === `form-builder.draft.${id}`) reload();
    };
    window.addEventListener("aivory-state-updated", onExternal);
    return () => window.removeEventListener("aivory-state-updated", onExternal);
  }, [id]);

  useEffect(() => {
    if (loading) return;
    const stateHash = JSON.stringify(state);
    if (stateHash === lastSavedHashRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setStatusLabel("Saving...");
        await saveFormDraft(id, state);
        lastSavedHashRef.current = stateHash;
        setStatusLabel("Saved");
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        statusTimerRef.current = setTimeout(() => setStatusLabel(""), 2500);
      } catch {
        setStatusLabel("Save failed");
      }
    }, 1200);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [id, state, loading]);

  const addCondition = (ruleKey) => {
    if (!activeField) return;
    const next = fields.map((f) => {
      if (f.id !== activeField.id) return f;
      const rule = f[ruleKey] || { logic: "AND", conditions: [] };
      return {
        ...f,
        [ruleKey]: {
          ...rule,
          conditions: [...(rule.conditions || []), { source: "answer", fieldId: "", op: "equals", value: "" }],
        },
      };
    });
    setFields(next);
  };

  const updateCondition = (ruleKey, idx, key, value) => {
    if (!activeField) return;
    const next = fields.map((f) => {
      if (f.id !== activeField.id) return f;
      const rule = f[ruleKey] || { logic: "AND", conditions: [] };
      const conditions = [...(rule.conditions || [])];
      conditions[idx] = { ...(conditions[idx] || {}), [key]: value };
      return { ...f, [ruleKey]: { ...rule, conditions } };
    });
    setFields(next);
  };

  const removeCondition = (ruleKey, idx) => {
    if (!activeField) return;
    const next = fields.map((f) => {
      if (f.id !== activeField.id) return f;
      const rule = f[ruleKey] || { logic: "AND", conditions: [] };
      return { ...f, [ruleKey]: { ...rule, conditions: (rule.conditions || []).filter((_, i) => i !== idx) } };
    });
    setFields(next);
  };

  const publishNow = async () => {
    setPublishing(true);
    try {
      const out = await publishForm(id);
      setState(out);
      toast.success("Form published", { description: `Version ${out.version} is live for attendee registration.` });
    } catch {
      toast.error("Could not publish form");
    } finally {
      setPublishing(false);
    }
  };

  const addField = (type) => {
    const newF = createDefaultField(type, fields.length);
    setFields([...fields, newF]);
    setActive(newF.id);
  };
  const remove = (id) => {
    const blockedBy = fields.filter((f) =>
      ["visibilityRule", "requiredRule"].some((rk) =>
        (f[rk]?.conditions || []).some((c) => c?.source !== "ticket" && c?.fieldId === id),
      ),
    );
    if (blockedBy.length) {
      toast.error("Field is referenced by active rules", {
        description: `Remove rule references in: ${blockedBy.map((b) => b.label || b.id).join(", ")}`,
      });
      return;
    }
    const next = fields.filter((f) => f.id !== id);
    setFields(next);
    if (active === id) setActive(next[0]?.id || null);
  };
  const update = (key, val) => setFields(fields.map((f) => (f.id === active ? { ...f, [key]: val } : f)));
  const moveField = (id, dir) => {
    const i = fields.findIndex((f) => f.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next.map((f, index) => ({ ...f, sortOrder: index })));
  };

  const builderFieldChoices = useMemo(
    () => fields.map((f) => ({ id: f.id, label: f.label || f.id })).filter((f) => f.id !== activeField?.id),
    [fields, activeField?.id],
  );

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="form-builder">
      {/* Toolbar */}
      <div className="border-b border-[var(--border-default)] px-4 md:px-6 min-h-[3.5rem] flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-overline truncate">{e.title} · form builder</div>
          <div className="text-sm text-[var(--text-primary)] hidden sm:block">Registration form</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {statusLabel ? <div className="text-xs font-mono text-[var(--text-secondary)] mr-1 hidden sm:block">{statusLabel}</div> : null}
          <button className="btn-ghost text-sm hidden sm:inline-flex"><Eye size={14} /> Preview</button>
          <button className="btn-primary shrink-0" onClick={publishNow} disabled={publishing}><Save size={14} /> <span className="hidden sm:inline">{publishing ? "Publishing..." : "Publish form"}</span></button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        {/* Left: field library */}
        <div className="hidden md:block col-span-3 border-r border-[var(--border-default)] overflow-y-auto p-5">
          <div className="text-overline mb-4">Field library</div>
          <div className="space-y-1">
            {FIELD_LIBRARY.map((f, idx) => (
              <button
                key={`${f.type}-${f.label}-${idx}`}
                onClick={() => addField(f.type)}
                data-testid={`add-field-${f.type}`}
                className="w-full text-left flex items-center justify-between px-3 py-2.5 surface-hover border border-transparent hover:border-[var(--border-default)] text-sm group"
              >
                <span>{f.label}</span>
                <Plus size={14} className="text-[var(--text-tertiary)] group-hover:text-[var(--primary)]" />
              </button>
            ))}
          </div>

          <div className="text-overline mt-10 mb-4">Conditional logic</div>
          <div className="surface p-4 text-xs text-[var(--text-secondary)] leading-relaxed">
            Rules support AND/OR groups with ticket and field-answer conditions. Requiredness can be separate from visibility.
            <div className="text-[var(--text-tertiary)] mt-2">Publish freezes the schema version used by registration.</div>
          </div>
        </div>

        {/* Center: canvas */}
        <div className="col-span-12 md:col-span-6 overflow-y-auto bg-[var(--bg-elev)] dot-grid p-4 md:p-12">
          <div className="max-w-md mx-auto bg-[#FAFAFA] text-[#09090B] shadow-[0_30px_60px_rgba(0,0,0,0.5)] p-8">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{e.title} — registration</div>
            <div className="font-heading text-2xl mt-1 tracking-tight">Registration details</div>
            {loading ? <div className="text-sm text-[var(--text-tertiary)]">Loading draft…</div> : null}
            <div className="mt-6 space-y-5">
              {fields.map((f) => (
                <div
                  key={f.id}
                  onClick={() => setActive(f.id)}
                  data-testid={`canvas-field-${f.id}`}
                  className={`group cursor-pointer relative ${active === f.id ? "ring-2 ring-[var(--primary)] ring-offset-4 ring-offset-[#FAFAFA]" : ""}`}
                >
                  <div className="text-[11px] uppercase tracking-widest text-[var(--text-tertiary)] font-medium flex items-center justify-between">
                    <input
                      value={f.label || ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setFields((prev) => prev.map((x) => (x.id === f.id ? { ...x, label: e.target.value } : x)))}
                      className="bg-transparent border-b border-[#09090B]/20 focus:border-[#09090B] focus:outline-none"
                    />
                    {f.required && <span className="text-[var(--error)] ml-1">*</span>}
                    {active === f.id && (
                      <span className="opacity-0 group-hover:opacity-100 flex items-center gap-2">
                        <GripVertical size={12} />
                        <button onClick={(e) => { e.stopPropagation(); moveField(f.id, -1); }} className="text-[var(--text-tertiary)]" aria-label="Move field up"><ChevronUp size={12} /></button>
                        <button onClick={(e) => { e.stopPropagation(); moveField(f.id, 1); }} className="text-[var(--text-tertiary)]" aria-label="Move field down"><ChevronDown size={12} /></button>
                        <button onClick={(e) => { e.stopPropagation(); remove(f.id); }} className="text-[var(--error)]"><Trash2 size={12} /></button>
                      </span>
                    )}
                  </div>
                  {f.type === "section" ? (
                    <div className="mt-2 text-sm text-[var(--text-tertiary)] border-l-2 border-[#09090B] pl-3">Section intro text</div>
                  ) : f.type === "checkbox_group" || f.type === "select" || f.type === "multiselect" || f.type === "dropdown" ? (
                    <select className="mt-2 w-full bg-transparent border-b border-[#09090B] py-2 text-sm focus:outline-none">
                      {(f.options || []).map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : f.type === "radio" || f.type === "yes_no" ? (
                    <div className="mt-2 space-y-1">
                      {(f.options || ["Option 1", "Option 2"]).map((o) => (
                        <label key={o} className="flex items-center gap-2 text-xs"><input type="radio" name={`fb-${f.id}`} />{o}</label>
                      ))}
                    </div>
                  ) : f.type === "rating" || f.type === "linear_scale" || f.type === "nps" ? (
                    <div className="mt-2 flex gap-1">
                      {(f.options || []).slice(0, 10).map((o) => (
                        <span key={o} className="text-xs px-2 py-1 border border-[#09090B]/30">{o}</span>
                      ))}
                    </div>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" /> {f.label}</label>
                  ) : f.type === "consent" ? (
                    <label className="flex items-start gap-2 mt-2 text-xs"><input type="checkbox" className="mt-0.5" /> I agree to terms and privacy policy.</label>
                  ) : f.type === "address" ? (
                    <input type="text" className="mt-2 w-full bg-transparent border-b border-[#09090B] py-2 text-sm focus:outline-none" placeholder="Street, city, postal" />
                  ) : f.type === "country" ? (
                    <select className="mt-2 w-full bg-transparent border-b border-[#09090B] py-2 text-sm focus:outline-none">
                      {(f.options || ["Singapore", "United States"]).map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : f.type === "file" ? (
                    <input type="file" className="mt-2 w-full text-xs" />
                  ) : f.type === "hidden" ? (
                    <div className="mt-2 text-xs text-[var(--text-tertiary)] font-mono">Hidden value (not shown to attendee)</div>
                  ) : f.type === "textarea" ? (
                    <textarea className="mt-2 w-full bg-transparent border-b border-[#09090B] py-2 text-sm focus:outline-none" rows={3} />
                  ) : (
                    <input
                      type={f.type === "email" ? "email" : f.type === "phone" ? "tel" : f.type === "number" ? "number" : f.type === "url" ? "url" : f.type === "date" ? "date" : f.type === "time" ? "time" : f.type === "datetime" ? "datetime-local" : "text"}
                      className="mt-2 w-full bg-transparent border-b border-[#09090B] py-2 text-sm focus:outline-none"
                    />
                  )}
                </div>
              ))}
            </div>
            <button className="mt-8 w-full bg-[var(--bg-base)] text-[var(--primary)] py-3 text-sm font-medium tracking-wide">Continue</button>
          </div>
        </div>

        {/* Right: properties */}
        <div className="hidden md:block col-span-3 border-l border-[var(--border-default)] overflow-y-auto p-5">
          <div className="flex items-center gap-2 text-overline mb-4"><Settings2 size={11} /> Properties</div>
          {activeField ? (
            <div className="space-y-4">
              <div>
                <label className="text-overline">Label</label>
                <input className="input-dark mt-2" value={activeField.label || ""} onChange={(e) => update("label", e.target.value)} data-testid="field-label-input" />
              </div>
              <div>
                <label className="text-overline">Field key</label>
                <input className="input-dark mt-2 font-mono text-xs" value={activeField.key || activeField.id || ""} onChange={(e) => update("key", e.target.value)} />
              </div>
              <div>
                <label className="text-overline">Type</label>
                <input className="input-dark mt-2 font-mono uppercase text-xs text-[var(--text-tertiary)]" value={activeField.type} disabled />
              </div>
              <div className="surface p-3 flex items-center justify-between">
                <span className="text-sm">Required</span>
                <button
                  onClick={() => update("required", !activeField.required)}
                  className={`w-9 h-5 transition relative ${activeField.required ? "bg-[var(--primary)]" : "bg-[var(--border-default)]"}`}
                  data-testid="field-required-toggle"
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-[var(--bg-base)] transition-all ${activeField.required ? "left-4" : "left-0.5"}`} />
                </button>
              </div>
              <div>
                <label className="text-overline">Required for tickets</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ticketTargets.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() =>
                        update(
                          "ticketScope",
                          (activeField.ticketScope || []).includes(t.id)
                            ? (activeField.ticketScope || []).filter((x) => x !== t.id)
                            : [...(activeField.ticketScope || []), t.id],
                        )
                      }
                      className={`text-xs font-mono px-2 py-1 border ${
                        (activeField.ticketScope || []).includes(t.id)
                          ? "border-[var(--primary)] text-[var(--primary)]"
                          : "border-[var(--border-default)] hover:border-[var(--primary)] hover:text-[var(--primary)]"
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-overline">Help text</label>
                <textarea className="input-dark mt-2" rows={3} placeholder="Optional helper text" value={activeField.helpText || ""} onChange={(e) => update("helpText", e.target.value)} />
              </div>
              <div>
                <label className="text-overline">Placeholder</label>
                <input className="input-dark mt-2" value={activeField.placeholder || ""} onChange={(e) => update("placeholder", e.target.value)} />
              </div>
              <div>
                <label className="text-overline">Content data source</label>
                <select className="input-dark mt-2" value={activeField.dataSourceKey || "manual"} onChange={(e) => update("dataSourceKey", e.target.value)}>
                  {DATA_SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
                <div className="text-[10px] text-[var(--text-tertiary)] mt-2 font-mono">
                  Select which source this field should pull from. Keep "Manual input" for user-entered values.
                </div>
              </div>
              {(["select", "dropdown", "multiselect", "checkbox_group", "radio", "yes_no", "linear_scale", "rating", "nps", "country"].includes(activeField.type)) && (
                <div>
                  <label className="text-overline">Options (one per line)</label>
                  <textarea
                    className="input-dark mt-2"
                    rows={5}
                    value={(activeField.options || []).join("\n")}
                    onChange={(e) =>
                      update(
                        "options",
                        e.target.value
                          .split("\n")
                          .map((x) => x.trim())
                          .filter(Boolean),
                      )
                    }
                  />
                </div>
              )}
              <RuleEditor
                title="Visibility rule"
                rule={activeField.visibilityRule || { logic: "AND", conditions: [] }}
                onLogicChange={(logic) => update("visibilityRule", { ...(activeField.visibilityRule || {}), logic })}
                onAdd={() => addCondition("visibilityRule")}
                onUpdate={(idx, key, value) => updateCondition("visibilityRule", idx, key, value)}
                onDelete={(idx) => removeCondition("visibilityRule", idx)}
                fields={builderFieldChoices}
              />
              <RuleEditor
                title="Requiredness rule"
                rule={activeField.requiredRule || { logic: "AND", conditions: [] }}
                onLogicChange={(logic) => update("requiredRule", { ...(activeField.requiredRule || {}), logic })}
                onAdd={() => addCondition("requiredRule")}
                onUpdate={(idx, key, value) => updateCondition("requiredRule", idx, key, value)}
                onDelete={(idx) => removeCondition("requiredRule", idx)}
                fields={builderFieldChoices}
              />
              <div className="surface p-3 text-xs text-[var(--text-secondary)] flex items-center gap-2">
                <CheckCircle2 size={13} className="text-[var(--primary)]" /> Published version: {state.version || 1}
                {state.publishedAt ? <span className="font-mono text-[var(--text-tertiary)]">· {new Date(state.publishedAt).toLocaleString()}</span> : <span>· never published</span>}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-tertiary)]">Select a field to edit its properties.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function RuleEditor({ title, rule, onLogicChange, onAdd, onUpdate, onDelete, fields }) {
  return (
    <div className="surface p-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-overline flex items-center gap-1"><GitBranch size={11} /> {title}</label>
        <select className="bg-transparent border border-[var(--border-default)] px-2 py-1 text-[10px] font-mono" value={rule.logic || "AND"} onChange={(e) => onLogicChange(e.target.value)}>
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      </div>
      {(rule.conditions || []).map((condition, idx) => (
        <div key={idx} className="grid grid-cols-4 gap-1">
          <select className="input-dark text-xs" value={condition.source || "answer"} onChange={(e) => onUpdate(idx, "source", e.target.value)}>
            <option value="answer">Answer</option>
            <option value="ticket">Ticket</option>
          </select>
          <select className="input-dark text-xs" value={condition.fieldId || ""} disabled={(condition.source || "answer") === "ticket"} onChange={(e) => onUpdate(idx, "fieldId", e.target.value)}>
            <option value="">Field</option>
            {fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <select className="input-dark text-xs" value={condition.op || "equals"} onChange={(e) => onUpdate(idx, "op", e.target.value)}>
            <option value="equals">equals</option>
            <option value="not_equals">not equals</option>
            <option value="contains">contains</option>
            <option value="not_empty">not empty</option>
            <option value="empty">empty</option>
          </select>
          <div className="flex gap-1">
            <input className="input-dark text-xs flex-1" value={condition.value || ""} onChange={(e) => onUpdate(idx, "value", e.target.value)} />
            <button type="button" className="btn-ghost px-1" onClick={() => onDelete(idx)}><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      <button type="button" className="btn-ghost text-xs px-0" onClick={onAdd}>+ Add condition</button>
    </div>
  );
}
